import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Button3D } from '@/components/ui/button-3d';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Card, CardContent } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import {
  Loader2,
  Building2,
  FileSignature,
  ShieldCheck,
  CheckCircle2,
  Clock,
  Ban,
  AlertTriangle,
} from 'lucide-react';
import { SignaturePad } from '@/components/quotes/SignaturePad';
import { formatDocument } from '@/lib/documentUtils';

interface PublicContract {
  title: string | null;
  content: string | null;
  total: number;
  clientName: string | null;
  hasDocumentChallenge: boolean;
  providerSignerName: string | null;
  providerSigned: boolean;
  status: string;
  companyName: string | null;
  companyLogoUrl: string | null;
  primaryColor: string | null;
}

interface Flags {
  signable: boolean;
  expired: boolean;
  alreadySigned: boolean;
  blocked: boolean;
}

const formatCurrency = (v: number) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v);

const SIGN_ERROR_MESSAGES: Record<string, string> = {
  consent_required: 'Marque a caixa de concordância para assinar.',
  name_required: 'Informe seu nome completo.',
  signature_required: 'Desenhe sua assinatura no quadro.',
  cpf_required: 'Informe o CPF/CNPJ.',
  not_configured: 'Este contrato não tem documento para conferência. Fale com quem enviou.',
  expired: 'O prazo para assinar este contrato expirou.',
  already_signed: 'Este contrato já foi assinado.',
  blocked: 'Muitas tentativas incorretas de documento. A assinatura foi bloqueada por segurança.',
  not_available: 'Este contrato não está mais disponível para assinatura.',
  server_error: 'Ocorreu um erro. Tente novamente.',
  internal_error: 'Ocorreu um erro. Tente novamente.',
};

export default function ContractSignPage() {
  const { token } = useParams<{ token: string }>();

  const [loading, setLoading] = useState(true);
  const [contract, setContract] = useState<PublicContract | null>(null);
  const [flags, setFlags] = useState<Flags | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  // Sign form state
  const [cpf, setCpf] = useState('');
  const [consent, setConsent] = useState(false);
  const [signerName, setSignerName] = useState('');
  const [signature, setSignature] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [attemptsLeft, setAttemptsLeft] = useState<number | null>(null);
  const [blocked, setBlocked] = useState(false);
  const [signedOk, setSignedOk] = useState(false);

  const load = useCallback(async () => {
    if (!token) {
      setLoadError('invalid_token');
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('contract-get', { body: { token } });
      if (error || !data || (data as any).ok !== true) {
        setLoadError((data as any)?.error ?? 'not_available');
        setLoading(false);
        return;
      }
      setContract((data as any).contract as PublicContract);
      setFlags((data as any).flags as Flags);
      setBlocked(Boolean((data as any).flags?.blocked));
    } catch {
      setLoadError('not_available');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    void load();
  }, [load]);

  const canEnableSignature = consent && signerName.trim().length >= 3;
  const canSubmit =
    canEnableSignature && Boolean(signature) && cpf.replace(/\D/g, '').length >= 11 && !submitting;

  const handleSubmit = async () => {
    if (!token || !canSubmit) return;
    setSubmitting(true);
    setFormError(null);
    try {
      const { data, error } = await supabase.functions.invoke('contract-sign', {
        body: {
          token,
          cpf: cpf.replace(/\D/g, ''),
          signerName: signerName.trim(),
          signatureDataUrl: signature,
          consent,
        },
      });

      const body = data as any;
      if (error || !body) {
        setFormError('Não foi possível assinar. Verifique sua conexão e tente novamente.');
        return;
      }

      if (body.ok === true) {
        setSignedOk(true);
        return;
      }

      // Domain errors (200 + ok:false + code).
      if (body.code === 'cpf_mismatch') {
        setAttemptsLeft(typeof body.attemptsLeft === 'number' ? body.attemptsLeft : null);
        setFormError('O documento informado não confere com o do contrato.');
        return;
      }
      if (body.code === 'blocked') {
        setBlocked(true);
        setFormError(SIGN_ERROR_MESSAGES.blocked);
        return;
      }
      if (body.code === 'already_signed') {
        // Refresh so the confirmation view takes over.
        await load();
        return;
      }
      setFormError(SIGN_ERROR_MESSAGES[body.code] ?? 'Não foi possível assinar. Tente novamente.');
    } catch {
      setFormError('Não foi possível assinar. Verifique sua conexão e tente novamente.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <CenteredScreen>
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <p className="text-muted-foreground">Carregando contrato…</p>
      </CenteredScreen>
    );
  }

  if (loadError || !contract || !flags) {
    return (
      <CenteredScreen>
        <FileSignature className="h-16 w-16 text-muted-foreground/50" />
        <h1 className="text-xl font-semibold">Contrato indisponível</h1>
        <p className="max-w-sm text-center text-muted-foreground">
          O link pode estar incorreto, expirado ou o contrato não está mais disponível para assinatura.
        </p>
      </CenteredScreen>
    );
  }

  const primaryColor = contract.primaryColor || '#7c3aed';
  const companyName = contract.companyName || 'Empresa';

  // Terminal states (nothing to sign).
  const terminal =
    signedOk || flags.alreadySigned
      ? 'signed'
      : blocked || flags.blocked
        ? 'blocked'
        : flags.expired
          ? 'expired'
          : !flags.signable
            ? 'unavailable'
            : null;

  return (
    <div className="min-h-screen bg-muted/30 pb-10">
      {/* Header */}
      <header className="sticky top-0 z-10 px-4 py-4" style={{ backgroundColor: primaryColor }}>
        <div className="mx-auto flex max-w-2xl items-center gap-4">
          {contract.companyLogoUrl ? (
            <img src={contract.companyLogoUrl} alt={companyName} className="h-12 w-12 rounded-xl bg-white/20 object-cover" />
          ) : (
            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-white/20">
              <Building2 className="h-6 w-6 text-white" />
            </div>
          )}
          <div className="min-w-0 flex-1">
            <h1 className="truncate font-semibold text-white">{companyName}</h1>
            <p className="text-sm text-white/80">Contrato para assinatura</p>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-2xl space-y-4 p-4">
        {/* Contract */}
        <Card>
          <CardContent className="space-y-3 p-4">
            <div>
              <h2 className="text-lg font-semibold">{contract.title || 'Contrato de prestação de serviço'}</h2>
              {contract.clientName && (
                <p className="text-sm text-muted-foreground">Contratante: {contract.clientName}</p>
              )}
            </div>
            <Separator />
            <p className="whitespace-pre-wrap text-xs leading-relaxed text-foreground/90">
              {contract.content || 'Sem conteúdo.'}
            </p>
            <Separator />
            <div className="flex items-center justify-between">
              <span className="font-semibold">Valor</span>
              <span className="text-lg font-bold" style={{ color: primaryColor }}>
                {formatCurrency(contract.total)}
              </span>
            </div>
          </CardContent>
        </Card>

        {/* Terminal states */}
        {terminal === 'signed' && (
          <StatusCard
            icon={<CheckCircle2 className="h-10 w-10 text-emerald-600" />}
            title="Contrato assinado!"
            description="Sua assinatura foi registrada com sucesso. Uma cópia fica com a empresa que enviou o contrato."
          />
        )}
        {terminal === 'blocked' && (
          <StatusCard
            icon={<Ban className="h-10 w-10 text-red-500" />}
            title="Assinatura bloqueada"
            description="Foram feitas muitas tentativas incorretas de documento. Entre em contato com quem enviou o contrato."
          />
        )}
        {terminal === 'expired' && (
          <StatusCard
            icon={<Clock className="h-10 w-10 text-amber-500" />}
            title="Prazo expirado"
            description="O prazo para assinar este contrato expirou. Peça um novo link a quem enviou o contrato."
          />
        )}
        {terminal === 'unavailable' && (
          <StatusCard
            icon={<AlertTriangle className="h-10 w-10 text-muted-foreground" />}
            title="Indisponível"
            description="Este contrato não está mais disponível para assinatura."
          />
        )}

        {/* Sign form */}
        {!terminal && (
          <Card>
            <CardContent className="space-y-4 p-4">
              <div className="flex items-start gap-2 rounded-md bg-muted p-3 text-xs text-muted-foreground">
                <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0" />
                <p>
                  Esta é uma <strong>assinatura eletrônica com trilha de evidências</strong> (data/hora, seu
                  documento, IP e um resumo do texto). Tem validade entre as partes (MP 2.200-2 / Lei
                  14.063/2020), mas <strong>não</strong> é uma assinatura digital ICP-Brasil (e-CPF).
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="cpf">Seu CPF ou CNPJ</Label>
                <Input
                  id="cpf"
                  inputMode="numeric"
                  value={cpf}
                  onChange={(e) => setCpf(formatDocument(e.target.value))}
                  placeholder="Confirme seu documento"
                  autoComplete="off"
                />
                <p className="text-xs text-muted-foreground">
                  Precisa ser o mesmo documento cadastrado no contrato — é assim que confirmamos que é você.
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="signer-name">Seu nome completo</Label>
                <Input
                  id="signer-name"
                  value={signerName}
                  onChange={(e) => setSignerName(e.target.value)}
                  placeholder="Nome de quem está assinando"
                />
              </div>

              <label className="flex items-start gap-2 text-sm">
                <Checkbox checked={consent} onCheckedChange={(v) => setConsent(v === true)} className="mt-0.5" />
                <span>Li e concordo com os termos deste contrato.</span>
              </label>

              <div className={canEnableSignature ? '' : 'pointer-events-none opacity-50'}>
                <SignaturePad label="Sua assinatura" onSignatureChange={setSignature} disabled={!canEnableSignature} />
                {!canEnableSignature && (
                  <p className="mt-1 text-xs text-muted-foreground">
                    Informe seu nome e marque a concordância para habilitar a assinatura.
                  </p>
                )}
              </div>

              {formError && (
                <div className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700 dark:bg-red-500/10 dark:text-red-400">
                  {formError}
                  {attemptsLeft != null && attemptsLeft > 0 && (
                    <span className="block text-xs">
                      {attemptsLeft} tentativa{attemptsLeft !== 1 ? 's' : ''} restante{attemptsLeft !== 1 ? 's' : ''}.
                    </span>
                  )}
                </div>
              )}

              <Button3D className="w-full" onClick={handleSubmit} disabled={!canSubmit}>
                {submitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <FileSignature className="mr-2 h-4 w-4" />}
                Assinar contrato
              </Button3D>
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
}

function StatusCard({ icon, title, description }: { icon: React.ReactNode; title: string; description: string }) {
  return (
    <Card>
      <CardContent className="flex flex-col items-center gap-3 py-10 text-center">
        {icon}
        <h2 className="text-lg font-semibold">{title}</h2>
        <p className="max-w-sm text-sm text-muted-foreground">{description}</p>
      </CardContent>
    </Card>
  );
}

function CenteredScreen({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-background p-4">
      {children}
    </div>
  );
}
